import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import axios from 'axios'
import { useFormik } from 'formik'
import { useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import * as Yup from 'yup'
import snackbarUtil from '../../app/snackbar/snackbarUtil'
import SignupLayout from './SignupLayout'
import SubdomainAvailabilityPill from './SubdomainAvailabilityPill'
import { checkSubdomain, getSignupCurrencies, registerTenant } from './signupApi'
import messages from './messages'
import { SignupCurrency, SubdomainStatus } from './types'

// Mirrors the server SubdomainValidator regex (lowercase RFC-1035 slug, 3-32 chars). Format is checked
// client-side before any availability call so a malformed slug doesn't hit the endpoint.
const SUBDOMAIN_RE = /^[a-z0-9][a-z0-9-]{1,30}[a-z0-9]$/
const DEBOUNCE_MS = 300

const SignupPage = () => {
  const { formatMessage } = useIntl()
  const navigate = useNavigate()
  const [currencies, setCurrencies] = useState<SignupCurrency[]>([])
  const [subdomainStatus, setSubdomainStatus] = useState<SubdomainStatus>('idle')
  const [submitError, setSubmitError] = useState<string | null>(null)

  const validationSchema = useMemo(
    () =>
      Yup.object({
        subdomain: Yup.string()
          .required()
          .matches(SUBDOMAIN_RE, formatMessage(messages.subdomainHelp)),
        email: Yup.string().required().email(),
        organisationName: Yup.string().required().max(250),
        currencyId: Yup.number().required(),
        acceptTerms: Yup.boolean().oneOf([true]),
      }),
    [formatMessage],
  )

  const formik = useFormik({
    initialValues: { subdomain: '', email: '', organisationName: '', currencyId: '' as number | '', acceptTerms: false },
    validationSchema,
    onSubmit: async (values) => {
      setSubmitError(null)
      try {
        await registerTenant({
          subdomain: values.subdomain.trim().toLowerCase(),
          email: values.email.trim(),
          organisationName: values.organisationName.trim(),
          currencyId: Number(values.currencyId),
          acceptTerms: values.acceptTerms,
        })
        navigate('/signup/check-email', { state: { email: values.email.trim() } })
      } catch (err) {
        // AC: render the server's message verbatim INLINE above submit (not a transient toast); the
        // form stays mounted for retry. The error body is the ResponseDto envelope
        // { data: { code, message } }; fall back to the axios message on a network error (no body).
        const body = axios.isAxiosError(err) ? err.response?.data : undefined
        setSubmitError(
          body?.data?.message ??
            body?.data?.detail ??
            (axios.isAxiosError(err) ? err.message : 'Something went wrong. Please try again.'),
        )
      }
    },
  })

  // Load the selectable currencies once (anonymous CORE endpoint).
  useEffect(() => {
    let active = true
    getSignupCurrencies()
      .then((list) => active && setCurrencies(list))
      .catch(() => active && snackbarUtil.error('Could not load currencies. Please refresh.'))
    return () => {
      active = false
    }
  }, [])

  // Debounced subdomain availability. Format is validated locally first; only well-formed slugs hit the
  // endpoint. An in-flight check is aborted when the value changes again.
  useEffect(() => {
    const value = formik.values.subdomain.trim().toLowerCase()
    if (value.length === 0) {
      setSubdomainStatus('idle')
      return
    }
    if (!SUBDOMAIN_RE.test(value)) {
      setSubdomainStatus('invalidFormat')
      return
    }

    setSubdomainStatus('checking')
    const controller = new AbortController()
    const timer = setTimeout(async () => {
      try {
        const res = await checkSubdomain(value, controller.signal)
        if (res.available) setSubdomainStatus('available')
        else if (res.reason === 'Reserved') setSubdomainStatus('reserved')
        else if (res.reason === 'InvalidFormat') setSubdomainStatus('invalidFormat')
        else setSubdomainStatus('taken') // Taken_Soft | Taken_Tenant
      } catch (err) {
        if (!axios.isCancel(err)) setSubdomainStatus('error')
      }
    }, DEBOUNCE_MS)

    return () => {
      controller.abort()
      clearTimeout(timer)
    }
  }, [formik.values.subdomain])

  // Clear a stale server-error Alert as soon as the user edits any field, so it can't keep referencing
  // something they've already corrected (it's re-set on the next submit). formik.values only changes
  // reference on an actual edit, so this doesn't wipe the error on the re-render that sets it.
  useEffect(() => {
    setSubmitError(null)
  }, [formik.values])

  const canSubmit = formik.isValid && formik.dirty && subdomainStatus === 'available' && !formik.isSubmitting

  return (
    <SignupLayout>
      <Typography variant='h5' gutterBottom>
        {formatMessage(messages.title)}
      </Typography>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
        {formatMessage(messages.subtitle)}
      </Typography>

      <form onSubmit={formik.handleSubmit}>
        <Stack spacing={2.5}>
          <Box>
            <TextField
              fullWidth
              name='subdomain'
              label={formatMessage(messages.subdomainLabel)}
              value={formik.values.subdomain}
              // Subdomains are always lowercase; normalise as the user types so the Yup rule and the
              // availability pill (which also lowercases) agree -- otherwise mixed-case input shows
              // "available" while the field flags a format error.
              onChange={(e) => formik.setFieldValue('subdomain', e.target.value.toLowerCase())}
              onBlur={formik.handleBlur}
              error={formik.touched.subdomain && Boolean(formik.errors.subdomain)}
              helperText={(formik.touched.subdomain && formik.errors.subdomain) || formatMessage(messages.subdomainHelp)}
            />
            <Box sx={{ mt: 1 }}>
              <SubdomainAvailabilityPill status={subdomainStatus} />
            </Box>
          </Box>

          <TextField
            fullWidth
            name='email'
            type='email'
            label={formatMessage(messages.emailLabel)}
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
          />

          <TextField
            fullWidth
            name='organisationName'
            label={formatMessage(messages.orgNameLabel)}
            value={formik.values.organisationName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.organisationName && Boolean(formik.errors.organisationName)}
            helperText={formik.touched.organisationName && formik.errors.organisationName}
          />

          <FormControl fullWidth error={formik.touched.currencyId && Boolean(formik.errors.currencyId)}>
            <InputLabel id='signup-currency-label'>{formatMessage(messages.currencyLabel)}</InputLabel>
            <Select
              labelId='signup-currency-label'
              name='currencyId'
              label={formatMessage(messages.currencyLabel)}
              value={formik.values.currencyId}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            >
              {currencies.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {/* Match the app-wide currency option format: `{iso3} - {localisedSymbol}` (e.g. RegisterCustomerModal).
                      Guard the nullable fields with ?? '' so a missing value never renders literal "undefined"/"null" (PriceDetails pattern). */}
                  {`${c.iso3 ?? ''} - ${c.localisedSymbol ?? ''}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box>
            <FormControlLabel
              control={<Checkbox name='acceptTerms' checked={formik.values.acceptTerms} onChange={formik.handleChange} />}
              label={formatMessage(messages.acceptTerms)}
            />
            {formik.touched.acceptTerms && Boolean(formik.errors.acceptTerms) && (
              <FormHelperText error>{formatMessage(messages.acceptTerms)}</FormHelperText>
            )}
          </Box>

          {submitError && (
            <Alert severity='error' role='alert'>
              {submitError}
            </Alert>
          )}

          <Button type='submit' variant='contained' size='large' disabled={!canSubmit}>
            {formatMessage(messages.submit)}
          </Button>
        </Stack>
      </form>
    </SignupLayout>
  )
}

export default SignupPage
